/**
 * Premium Page — subscription status, feature comparison, dynamic pricing.
 * Prices fetched from pricing table via usePricing(), never hardcoded.
 */

import React, { useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X, Crown, Check, Infinity as InfinityIcon, Target, Users, MessageCircle,
  Sparkles, ArrowRight, Shield, Gift, Loader2, CheckCircle2, AlertCircle,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@turnifly/shared/ui'
import { Badge } from '@turnifly/shared/ui'
import { useAuthStore } from '@turnifly/shared/stores'
import { useProfile } from '@turnifly/shared/hooks'
import { useActiveSubscription, usePricing, useValidatePromo, useRedeemGift } from '@turnifly/shared/hooks'
import { format } from 'date-fns'
import { triggerHapticImpact as hapticImpact, triggerHapticNotification as hapticNotification } from '@turnifly/shared/utils'
import { PaymentCheckoutPage } from './payment-checkout-page'

interface PremiumPageProps {
  isOpen: boolean
  onClose: () => void
}

interface FeatureRow {
  icon: React.ReactNode
  label: string
  free: boolean | string
}

export const PremiumPage: React.FC<PremiumPageProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const { data: profile } = useProfile(user?.id)
  const { data: subscription } = useActiveSubscription(user?.id)
  const { data: pricingList } = usePricing()
  const [promoCode, setPromoCode] = useState('')
  const [selectedPeriod, setSelectedPeriod] = useState<'monthly' | 'yearly'>('yearly')
  const [showCheckout, setShowCheckout] = useState(false)

  const validatePromo = useValidatePromo()
  const redeemGift = useRedeemGift()
  const [promoStatus, setPromoStatus] = useState<'idle' | 'checking' | 'valid' | 'gift' | 'error' | 'redeemed'>('idle')
  const [promoMessage, setPromoMessage] = useState('')
  const [giftPlan, setGiftPlan] = useState<string | null>(null)

  const handleCheckPromo = async (): Promise<void> => {
    const code = promoCode.trim()
    if (!code) return
    hapticImpact('light')
    setPromoStatus('checking')
    setPromoMessage('')
    try {
      const result = await validatePromo.mutateAsync(code)
      if (!result.valid) {
        setPromoStatus('error')
        setPromoMessage(result.error ?? t('premium.promoInvalid'))
        hapticNotification('error')
        return
      }
      if (result.type === 'gift') {
        setPromoStatus('gift')
        setGiftPlan(result.plan ?? null)
        setPromoMessage(t('premium.giftFound'))
        hapticNotification('success')
      } else {
        setPromoStatus('valid')
        setPromoMessage(t('premium.promoApplied'))
        hapticNotification('success')
      }
    } catch {
      setPromoStatus('error')
      setPromoMessage(t('premium.promoInvalid'))
      hapticNotification('error')
    }
  }

  const handleRedeemGift = async (): Promise<void> => {
    const code = promoCode.trim()
    if (!code) return
    hapticImpact('medium')
    setPromoStatus('checking')
    try {
      const result = await redeemGift.mutateAsync(code)
      if (result.success) {
        setPromoStatus('redeemed')
        setPromoMessage(
          result.expires_at
            ? t('premium.giftRedeemed', { date: format(new Date(result.expires_at), 'dd.MM.yyyy') })
            : t('premium.giftRedeemedSimple'),
        )
        hapticNotification('success')
      } else {
        setPromoStatus('error')
        setPromoMessage(result.error ?? t('premium.promoInvalid'))
        hapticNotification('error')
      }
    } catch {
      setPromoStatus('error')
      setPromoMessage(t('premium.promoInvalid'))
      hapticNotification('error')
    }
  }

  const resetPromo = (): void => {
    setPromoStatus('idle')
    setPromoMessage('')
    setGiftPlan(null)
  }

  const isPremium = profile?.is_premium ?? false

  const features = useMemo((): FeatureRow[] => [
    { icon: <InfinityIcon size={16} />, label: t('premium.unlimitedHabits'), free: '5' },
    { icon: <Target size={16} />, label: t('premium.goals'), free: false },
    { icon: <Shield size={16} />, label: t('premium.privateChallenge'), free: false },
    { icon: <Users size={16} />, label: t('premium.unlimitedChallenges'), free: '1' },
    { icon: <MessageCircle size={16} />, label: t('premium.directMessages'), free: false },
    { icon: <Sparkles size={16} />, label: t('premium.lifeSection'), free: false },
    { icon: <Crown size={16} />, label: t('premium.referralWithdraw'), free: false },
  ], [t])

  // Get UZS pricing from DB
  const monthlyPrice = pricingList?.find(
    (p) => p.plan === 'premium' && p.period === 'monthly' && p.currency === 'UZS',
  )
  const yearlyPrice = pricingList?.find(
    (p) => p.plan === 'premium' && p.period === 'yearly' && p.currency === 'UZS',
  )

  // Calculate discount dynamically
  const discount = monthlyPrice && yearlyPrice
    ? Math.round(100 - (yearlyPrice.amount / (monthlyPrice.amount * 12)) * 100)
    : 0

  const handleContinue = (): void => {
    hapticImpact('medium')
    setShowCheckout(true)
  }

  const handlePaymentSuccess = (): void => {
    setShowCheckout(false)
    onClose()
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          <motion.div
            initial={{ y: '100%', opacity: 0.8 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0.8 }}
            transition={{ type: 'tween', duration: 0.25, ease: 'easeInOut' }}
            className="
              fixed z-50 bg-bg flex flex-col
              inset-x-0 bottom-0 rounded-t-[32px] max-h-[92vh]
              lg:inset-auto lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2
              lg:rounded-3xl lg:max-h-[85vh] lg:w-full lg:max-w-md
              lg:border lg:border-white/[0.06] lg:shadow-glass
            "
          >
            {/* Header — safe area for fullscreen */}
            <div className="flex items-center justify-between px-5 pb-2 shrink-0" style={{ paddingTop: 'max(20px, calc(var(--tg-safe-area-inset-top) + 8px))' }}>
              <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/[0.04] tap-highlight-transparent">
                <X size={20} className="text-text" />
              </button>
              <h2 className="font-display font-bold text-lg text-text">Premium</h2>
              <div className="w-10" />
            </div>

            <div className="flex-1 overflow-y-auto px-5 pb-8">
              {/* Crown Hero */}
              <div className="flex flex-col items-center py-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400/20 to-amber-600/20 flex items-center justify-center mb-3">
                  <Crown size={28} className="text-amber-400" />
                </div>
                <h3 className="text-xl font-display font-bold text-text mb-1">
                  {isPremium ? t('premium.youArePremium') : t('premium.unlockAll')}
                </h3>
                {isPremium && (
                  <Badge variant="success">{t('premium.active')}</Badge>
                )}
              </div>

              {/* Active subscription details */}
              {isPremium && subscription && (
                <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4 mt-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-hint">{t('premium.plan')}</span>
                    <span className="text-text font-medium capitalize">{subscription.plan}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-2">
                    <span className="text-text-hint">{t('premium.expiresAt')}</span>
                    <span className="text-text font-medium">
                      {format(new Date(subscription.expires_at), 'dd.MM.yyyy')}
                    </span>
                  </div>
                  {subscription.auto_renew && (
                    <div className="flex items-center justify-between text-sm mt-2">
                      <span className="text-text-hint">{t('premium.autoRenew')}</span>
                      <Badge variant="success" className="text-[10px]">{t('common.on')}</Badge>
                    </div>
                  )}
                </div>
              )}

              {/* Feature Comparison */}
              <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] overflow-hidden mt-4">
                <div className="grid grid-cols-[1fr_60px_60px] px-4 py-2.5 border-b border-white/[0.04]">
                  <span className="text-xs text-text-hint font-medium">{t('premium.feature')}</span>
                  <span className="text-xs text-text-hint font-medium text-center">{t('common.free')}</span>
                  <span className="text-xs text-amber-400 font-medium text-center">Pro</span>
                </div>

                {features.map((row, i) => (
                  <div
                    key={i}
                    className="grid grid-cols-[1fr_60px_60px] px-4 py-3 border-b border-white/[0.02] last:border-b-0"
                  >
                    <div className="flex items-center gap-2 text-sm text-text">
                      <span className="text-text-hint">{row.icon}</span>
                      {row.label}
                    </div>
                    <div className="flex justify-center items-center">
                      {typeof row.free === 'string' ? (
                        <span className="text-xs text-text-hint">{row.free}</span>
                      ) : (
                        <span className="text-xs text-text-hint/30">—</span>
                      )}
                    </div>
                    <div className="flex justify-center items-center">
                      <Check size={14} className="text-amber-400" />
                    </div>
                  </div>
                ))}
              </div>

              {/* Plan Selection — both free and premium users can buy/extend */}
              {(monthlyPrice || yearlyPrice) && (
                <div className="mt-5 space-y-3">
                  {/* Monthly */}
                  {monthlyPrice && (
                    <button
                      onClick={() => { setSelectedPeriod('monthly'); hapticImpact('light') }}
                      className={`w-full p-4 rounded-2xl text-left transition-colors duration-200 relative border-2 ${
                        selectedPeriod === 'monthly'
                          ? 'bg-white/[0.06] border-primary/40'
                          : 'bg-white/[0.03] border-white/[0.06]'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="text-base font-display font-bold text-text">{t('premium.monthlyTitle')}</h4>
                          <p className="text-xs text-text-hint mt-0.5">{t('premium.monthlyDesc')}</p>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 ${
                          selectedPeriod === 'monthly' ? 'border-primary bg-primary' : 'border-white/[0.2]'
                        }`}>
                          {selectedPeriod === 'monthly' && <Check size={12} className="text-white" />}
                        </div>
                      </div>
                      <div className="mt-3 flex items-baseline gap-2">
                        <span className="text-xl font-display font-bold text-text">
                          {formatPrice(monthlyPrice.amount)} UZS
                        </span>
                        <span className="text-sm text-text-hint">{t('premium.perMonth')}</span>
                      </div>
                    </button>
                  )}

                  {/* Yearly */}
                  {yearlyPrice && monthlyPrice && (
                    <div className="relative">
                      <Badge variant="warning" className="absolute -top-2.5 right-4 z-10 text-[10px] px-3">
                        {t('premium.popular')}
                      </Badge>
                      <button
                        onClick={() => { setSelectedPeriod('yearly'); hapticImpact('light') }}
                        className={`w-full p-4 rounded-2xl text-left transition-colors duration-200 border-2 ${
                          selectedPeriod === 'yearly'
                            ? 'bg-gradient-to-br from-amber-400/10 to-amber-600/5 border-amber-400/30'
                            : 'bg-white/[0.03] border-white/[0.06]'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="text-base font-display font-bold text-text">{t('premium.yearlyTitle')}</h4>
                              {discount > 0 && (
                                <Badge variant="success" className="text-[10px]">
                                  {discount}% {t('premium.cheaper')}
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-text-hint mt-0.5">{t('premium.yearlyDesc')}</p>
                          </div>
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 ${
                            selectedPeriod === 'yearly' ? 'border-amber-400 bg-amber-400' : 'border-white/[0.2]'
                          }`}>
                            {selectedPeriod === 'yearly' && <Check size={12} className="text-black" />}
                          </div>
                        </div>
                        <div className="mt-3 flex items-baseline gap-2">
                          <span className="text-xl font-display font-bold text-text">
                            {formatPrice(yearlyPrice.amount)} UZS
                          </span>
                          <span className="text-sm text-text-hint line-through">
                            {formatPrice(monthlyPrice.amount * 12)} UZS
                          </span>
                        </div>
                        <p className="text-xs text-amber-400 mt-1">
                          {formatPrice(Math.round(yearlyPrice.amount / 12))} UZS {t('premium.yearlyPerMonth')}
                        </p>
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Promo / Gift code input */}
              <div className="mt-4">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Gift size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-hint/40" />
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); resetPromo() }}
                      placeholder={t('premium.promoPlaceholder')}
                      disabled={promoStatus === 'checking' || promoStatus === 'redeemed'}
                      className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.06]
                        text-text text-sm font-mono tracking-wider placeholder:text-text-hint/40
                        focus:outline-none focus:border-primary/30 disabled:opacity-50"
                    />
                  </div>
                  {promoCode.trim().length >= 4 && promoStatus !== 'gift' && promoStatus !== 'redeemed' && (
                    <button
                      onClick={handleCheckPromo}
                      disabled={promoStatus === 'checking'}
                      className="shrink-0 px-4 py-3 rounded-xl bg-primary/10 border border-primary/20
                        text-primary text-sm font-medium transition-colors hover:bg-primary/20
                        disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {promoStatus === 'checking' ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        t('premium.checkCode')
                      )}
                    </button>
                  )}
                </div>

                {/* Status message */}
                {promoMessage && promoStatus !== 'gift' && promoStatus !== 'redeemed' && (
                  <div className={`flex items-center gap-2 mt-2 text-xs ${
                    promoStatus === 'valid' ? 'text-emerald-400' : 'text-red-400'
                  }`}>
                    {promoStatus === 'valid' && <CheckCircle2 size={14} />}
                    {promoStatus === 'error' && <AlertCircle size={14} />}
                    <span>{promoMessage}</span>
                  </div>
                )}

                {/* Gift code found — show redeem card */}
                {promoStatus === 'gift' && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-3 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-emerald-600/5
                      border border-emerald-500/20 p-4"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Gift size={18} className="text-emerald-400" />
                      <span className="text-sm font-medium text-emerald-400">{t('premium.giftFound')}</span>
                    </div>
                    <p className="text-xs text-text-hint mb-3">
                      {giftPlan === 'yearly' ? t('premium.giftYearly') : t('premium.giftMonthly')}
                    </p>
                    <Button
                      variant="primary"
                      fullWidth
                      size="md"
                      onClick={handleRedeemGift}
                      disabled={redeemGift.isPending}
                    >
                      {redeemGift.isPending ? (
                        <Loader2 size={16} className="animate-spin mr-2" />
                      ) : (
                        <Gift size={16} className="mr-2" />
                      )}
                      {t('premium.redeemGift')}
                    </Button>
                  </motion.div>
                )}

                {/* Gift redeemed success */}
                {promoStatus === 'redeemed' && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="mt-3 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-emerald-600/5
                      border border-emerald-500/20 p-4 text-center"
                  >
                    <CheckCircle2 size={32} className="text-emerald-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-emerald-400">{t('premium.giftActivated')}</p>
                    <p className="text-xs text-text-hint mt-1">{promoMessage}</p>
                  </motion.div>
                )}
              </div>
            </div>

            {/* Continue Button — with bottom safe area */}
            <div className="px-5 pt-3 shrink-0 border-t border-white/[0.04]" style={{ paddingBottom: 'max(24px, calc(var(--tg-safe-area-inset-bottom) + 12px))' }}>
              <Button variant="primary" fullWidth size="lg" onClick={handleContinue}>
                {isPremium ? t('premium.extend') : t('premium.continue')}
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </div>
          </motion.div>

          {/* Payment Checkout */}
          <PaymentCheckoutPage
            isOpen={showCheckout}
            onClose={() => setShowCheckout(false)}
            plan="premium"
            period={selectedPeriod}
            promoCode={promoCode.trim() || undefined}
            onSuccess={handlePaymentSuccess}
          />
        </>
      )}
    </AnimatePresence>
  )
}

// ── Helpers ─────────────────────────────────────────────────

function formatPrice(amount: number): string {
  return amount.toLocaleString('uz-UZ')
}
